import LiquidText from '../components/LiquidText';
import DeleteIcon from '../components/DeleteIcon';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import { useLiquidFeedback } from '../components/LiquidUI';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import MapSearch from '../components/MapSearch';
import WorkItemsManager from '../components/WorkItemsManager';
import './NovaObra.css';

const NovaObra = () => {
    const liquidFeedback = useLiquidFeedback();
    const navigate = useNavigate();
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const mainMarkerRef = useRef(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const PREDEFINED_COLORS = [
        '#3182ce', '#38a169', '#dd6b20', '#e53e3e',
        '#805ad5', '#d69e2e', '#319795', '#718096'
    ];

    // UI State
    const [showWorkManager, setShowWorkManager] = useState(false);
    const [availableWorkItems, setAvailableWorkItems] = useState([]);

    // Map State
    const [mapTools, setMapTools] = useState({
        activeTool: null,
        polygons: { red: [], yellow: [] },
        icons: [],
        drawnLayers: []
    });
    const [currentPolygonPoints, setCurrentPolygonPoints] = useState([]);

    const [users, setUsers] = useState([]);
    const [clientes, setClientes] = useState([]);

    const [formData, setFormData] = useState({
        posicao: '',
        cliente: '',
        cliente_id: '',
        data_inicio: '',
        data_fim: '',
        descricao: '',
        tipo_obra: 'obra',
        tipo_trabalho: [],
        latitude: 38.7223,
        longitude: -9.1393,
        cor: '#3182ce',
        work_quantities: {}, // { id: quantity }
        observacoes: '',
        gestor_cliente: '',
        responsavel_cliente_id: '',
        email_responsavel_cliente: '',
        telefone_responsavel_cliente: '',
        responsavel_obra_id: '',
        email_responsavel: '',
        telefone_responsavel: '',
        seguranca_obra_id: '',
        email_seguranca: '',
        telefone_seguranca: '',
        documentacao_validada: '',
        necessidade_at: '',
        data_at: '',
        hora_at: '',
        tipo_at: '',
        nome_fiscalizacao: '',
        email_fiscalizacao: '',
        telefone_fiscalizacao: '',
        fiscalizacao_id: '',
        gestor_cliente_id: ''
    });

    // Load work items, users and clientes
    useEffect(() => {
        fetchWorkItems();
        fetchUsers();
        api.get('/clientes').then(r => setClientes(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await api.get('/auth/users');
            setUsers(res.data.users || res.data);
        } catch (err) {
            console.error('Error loading users:', err);
        }
    };

    const fetchWorkItems = async () => {
        try {
            const res = await api.get('/config/trabalhos');
            setAvailableWorkItems(res.data);
        } catch (err) {
            console.error('Error loading work items:', err);
        }
    };

    // Initialize Map - ONLY ONCE
    useEffect(() => {
        if (mapLoaded || mapInstanceRef.current) return;

        const loadMap = async () => {
            if (!mapRef.current) return;

            try {
                const L = (await import('leaflet')).default;
                await import('leaflet/dist/leaflet.css');

                delete L.Icon.Default.prototype._getIconUrl;
                L.Icon.Default.mergeOptions({
                    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
                    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
                });

                // Use default coordinates for initial map load
                const map = L.map(mapRef.current, {
                    center: [38.7223, -9.1393],
                    zoom: 18
                });

                L.tileLayer('https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}', {
                    attribution: '&copy; Google Maps',
                    maxZoom: 21
                }).addTo(map);

                const mainMarker = L.marker([38.7223, -9.1393], { draggable: true })
                    .addTo(map)
                    .bindPopup("Localização da Obra");

                mainMarker.on('dragstart', function (e) {
                    window.dragStartPos = e.target.getLatLng();
                });

                mainMarker.on('dragend', function (e) {
                    const newPos = e.target.getLatLng();
                    const oldPos = window.dragStartPos || newPos;

                    const deltaLat = newPos.lat - oldPos.lat;
                    const deltaLng = newPos.lng - oldPos.lng;

                    // Atualizar coordenadas no form
                    setFormData(prev => ({
                        ...prev,
                        latitude: newPos.lat,
                        longitude: newPos.lng
                    }));

                    // MOVER TODOS OS DESENHOS (Polígonos e Ícones) junto com o marcador
                    setMapTools(prev => {
                        const newPolygons = {
                            red: prev.polygons.red.map(poly => poly.map(p => [p[0] + deltaLat, p[1] + deltaLng])),
                            yellow: prev.polygons.yellow.map(poly => poly.map(p => [p[0] + deltaLat, p[1] + deltaLng]))
                        };
                        const newIcons = prev.icons.map(icon => ({
                            ...icon,
                            lat: icon.lat + deltaLat,
                            lng: icon.lng + deltaLng
                        }));

                        // Atualizar as camadas visuais no mapa
                        prev.drawnLayers.forEach(layer => {
                            if (layer.setLatLng) { // Ícones (Markers)
                                const cur = layer.getLatLng();
                                layer.setLatLng([cur.lat + deltaLat, cur.lng + deltaLng]);
                            } else if (layer.getLatLngs) { // Polígonos
                                const latlngs = layer.getLatLngs();
                                // Leaflet polygons podem ter arrays aninhados
                                const newLatlngs = latlngs.map(ring =>
                                    Array.isArray(ring)
                                        ? ring.map(p => ({ lat: p.lat + deltaLat, lng: p.lng + deltaLng }))
                                        : { lat: ring.lat + deltaLat, lng: ring.lng + deltaLng }
                                );
                                layer.setLatLngs(newLatlngs);
                            }
                        });

                        return {
                            ...prev,
                            polygons: newPolygons,
                            icons: newIcons
                        };
                    });
                });

                mainMarkerRef.current = mainMarker;
                mapInstanceRef.current = map;
                setMapLoaded(true);
            } catch (error) {
                console.error('Error loading map:', error);
            }
        };

        loadMap();

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
                setMapLoaded(false);
            }
        };
    }, []); // Empty dependency - map loads ONLY once

    // Handle drawing tools
    useEffect(() => {
        if (!mapInstanceRef.current || !mapLoaded) return;

        const map = mapInstanceRef.current;

        const handleMapClick = async (e) => {
            if (!mapTools.activeTool) return;

            const L = (await import('leaflet')).default;
            const { lat, lng } = e.latlng;

            if (mapTools.activeTool.startsWith('polygon')) {
                // Simple polygon drawing - click to add points
                const color = mapTools.activeTool === 'polygon-red' ? '#ff0000' : '#ffff00';

                if (!window.currentPolygonPoints) {
                    window.currentPolygonPoints = [];
                    window.currentPolygonMarkers = [];
                }

                window.currentPolygonPoints.push([lat, lng]);
                setCurrentPolygonPoints([...window.currentPolygonPoints]); // Update state for UI

                // Add temporary marker
                const marker = L.circleMarker([lat, lng], {
                    radius: 4,
                    color: color,
                    fillColor: color,
                    fillOpacity: 1
                }).addTo(map);
                window.currentPolygonMarkers.push(marker);

                // Draw line if we have at least 2 points
                if (window.currentPolygonPoints.length >= 2) {
                    if (window.currentPolyline) {
                        map.removeLayer(window.currentPolyline);
                    }
                    window.currentPolyline = L.polyline(window.currentPolygonPoints, {
                        color: color,
                        weight: 2
                    }).addTo(map);
                }
            } else if (mapTools.activeTool?.startsWith('icon-')) {
                // Add icon marker - supports multiple icon types
                const iconType = mapTools.activeTool.replace('icon-', '');
                const iconConfig = {
                    'cone': { emoji: '🚧', bg: '#FF9800' },
                    'health': { emoji: '✚', bg: '#ffffff', color: '#00aa00', border: '2px solid #00aa00' },
                    'extinguisher': { emoji: '🧯', bg: '#F44336' },
                    'start': { emoji: '⚠️', bg: '#FFC107' },
                    'end': { emoji: '🛑', bg: '#F44336' },
                    'warning': { emoji: '⚠️', bg: '#FFC107' }
                };

                const config = iconConfig[iconType] || { emoji: '📍', bg: '#9E9E9E' };

                const marker = L.marker([lat, lng], {
                    icon: L.divIcon({
                        html: `<div style="
                            background: ${config.bg};
                            color: ${config.color || 'black'};
                            border: ${config.border || '2px solid white'};
                            border-radius: 8px;
                            width: 32px;
                            height: 32px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 20px;
                            box-shadow: 0 2px 5px rgba(0,0,0,0.3);
                        ">${config.emoji}</div>`,
                        className: 'custom-map-icon',
                        iconSize: [32, 32],
                        iconAnchor: [16, 16]
                    })
                }).addTo(map);

                setMapTools(prev => ({
                    ...prev,
                    icons: [...prev.icons, { lat, lng, type: iconType }],
                    drawnLayers: [...prev.drawnLayers, marker]
                }));
            }
        };

        const handleMapDblClick = async (e) => {
            if (mapTools.activeTool?.startsWith('polygon') && window.currentPolygonPoints && window.currentPolygonPoints.length >= 3) {
                const finalPoints = [...window.currentPolygonPoints]; // Capturar cópia antes de limpar
                const L = (await import('leaflet')).default;
                const color = mapTools.activeTool === 'polygon-red' ? '#ff0000' : '#ffff00';
                const polygonType = mapTools.activeTool === 'polygon-red' ? 'red' : 'yellow';

                // Remove temporary markers and polyline
                window.currentPolygonMarkers?.forEach(m => map.removeLayer(m));
                if (window.currentPolyline) map.removeLayer(window.currentPolyline);

                // Create final polygon
                const polygon = L.polygon(finalPoints, {
                    color: color,
                    fillColor: color,
                    fillOpacity: 0.3,
                    weight: 2
                }).addTo(map);

                setMapTools(prev => ({
                    ...prev,
                    polygons: {
                        ...prev.polygons,
                        [polygonType]: [...prev.polygons[polygonType], finalPoints]
                    },
                    drawnLayers: [...prev.drawnLayers, polygon],
                    activeTool: null
                }));

                // Reset drawing state
                window.currentPolygonPoints = null;
                window.currentPolygonMarkers = null;
                window.currentPolyline = null;
                setCurrentPolygonPoints([]);
            }
        };

        map.on('click', handleMapClick);
        map.on('dblclick', handleMapDblClick);

        return () => {
            map.off('click', handleMapClick);
            map.off('dblclick', handleMapDblClick);
        };
    }, [mapTools.activeTool, mapLoaded]);

    // Clear temporary drawing state when switching tools
    useEffect(() => {
        if (mapInstanceRef.current && mapLoaded) {
            const map = mapInstanceRef.current;
            // Clear temporary markers and polyline when changing tool
            if (window.currentPolygonMarkers) {
                window.currentPolygonMarkers.forEach(m => {
                    try { map.removeLayer(m); } catch (e) { }
                });
            }
            if (window.currentPolyline) {
                try { map.removeLayer(window.currentPolyline); } catch (e) { }
            }
            window.currentPolygonPoints = null;
            window.currentPolygonMarkers = null;
            window.currentPolyline = null;
        }
    }, [mapTools.activeTool]);

    // Clear drawn layers when clearing tools
    useEffect(() => {
        if (mapTools.drawnLayers.length === 0 && mapInstanceRef.current) {
            // Clear any temporary drawing state
            const map = mapInstanceRef.current;
            window.currentPolygonMarkers?.forEach(m => map.removeLayer(m));
            if (window.currentPolyline) map.removeLayer(window.currentPolyline);
            window.currentPolygonPoints = null;
            window.currentPolygonMarkers = null;
            window.currentPolyline = null;

            // Remove all drawn layers
            mapTools.drawnLayers.forEach(layer => {
                try {
                    map.removeLayer(layer);
                } catch (e) {
                    // Layer might already be removed
                }
            });
        }
    }, [mapTools.drawnLayers.length]);

    // Function to manually finish polygon (alternative to double-click)
    const finishPolygon = async () => {
        if (!window.currentPolygonPoints || window.currentPolygonPoints.length < 3) {
            liquidFeedback.notify('Precisa de pelo menos 3 pontos para criar um polígono!');
            return;
        }

        const finalPoints = [...window.currentPolygonPoints]; // Capturar cópia antes de limpar
        const L = (await import('leaflet')).default;
        const map = mapInstanceRef.current;
        const color = mapTools.activeTool === 'polygon-red' ? '#ff0000' : '#ffff00';
        const polygonType = mapTools.activeTool === 'polygon-red' ? 'red' : 'yellow';

        // Remove temporary markers and polyline
        window.currentPolygonMarkers?.forEach(m => map.removeLayer(m));
        if (window.currentPolyline) map.removeLayer(window.currentPolyline);

        // Create final polygon
        const polygon = L.polygon(finalPoints, {
            color: color,
            fillColor: color,
            fillOpacity: 0.3,
            weight: 2
        }).addTo(map);

        setMapTools(prev => ({
            ...prev,
            polygons: {
                ...prev.polygons,
                [polygonType]: [...prev.polygons[polygonType], finalPoints]
            },
            drawnLayers: [...prev.drawnLayers, polygon],
            activeTool: null
        }));

        // Reset drawing state
        window.currentPolygonPoints = null;
        window.currentPolygonMarkers = null;
        window.currentPolyline = null;
        setCurrentPolygonPoints([]);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'tipo_obra') {
            setFormData(prev => ({ ...prev, tipo_obra: value, cor: value === 'pendencia' ? '#e53e3e' : value === 'manutencao' ? '#805ad5' : '#3182ce' }));
            return;
        }

        // Auto-fill details when selecting users
        if (name === 'fiscalizacao_id') {
            const user = users.find(u => u.id === parseInt(value));
            setFormData(prev => ({
                ...prev,
                [name]: value,
                nome_fiscalizacao: user ? user.nome_completo : '',
                email_fiscalizacao: user ? user.email : '',
                telefone_fiscalizacao: user ? (user.telefone || '') : ''
            }));
        } else if (name === 'responsavel_obra_id') {
            const user = users.find(u => u.id === parseInt(value));
            setFormData(prev => ({
                ...prev,
                [name]: value,
                email_responsavel: user ? user.email : '',
                telefone_responsavel: user ? (user.telefone || '') : ''
            }));
        } else if (name === 'seguranca_obra_id') {
            const user = users.find(u => u.id === parseInt(value));
            setFormData(prev => ({
                ...prev,
                [name]: value,
                email_seguranca: user ? user.email : '',
                telefone_seguranca: user ? (user.telefone || '') : ''
            }));
        } else if (name === 'gestor_cliente_id') {
            const user = users.find(u => u.id === parseInt(value));
            setFormData(prev => ({
                ...prev,
                [name]: value,
                gestor_cliente: user ? user.nome_completo : ''
            }));
        } else if (name === 'responsavel_cliente_id') {
            const user = users.find(u => u.id === parseInt(value));
            setFormData(prev => ({
                ...prev,
                [name]: value,
                email_responsavel_cliente: user ? user.email : '',
                telefone_responsavel_cliente: user ? (user.telefone || '') : ''
            }));
        } else if (name === 'cliente_id') {
            const c = clientes.find(cl => cl.id === parseInt(value));
            setFormData(prev => ({
                ...prev,
                cliente_id: value,
                cliente: c ? c.nome : ''
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleWorkItemToggle = (itemId) => {
        setFormData(prev => {
            const isSelected = prev.tipo_trabalho.includes(itemId);
            const newTipoTrabalho = isSelected
                ? prev.tipo_trabalho.filter(t => t !== itemId)
                : [...prev.tipo_trabalho, itemId];

            const newWorkQuantities = { ...prev.work_quantities };
            if (!isSelected) {
                newWorkQuantities[itemId] = 1; // Default quantity
            } else {
                delete newWorkQuantities[itemId];
            }

            return {
                ...prev,
                tipo_trabalho: newTipoTrabalho,
                work_quantities: newWorkQuantities
            };
        });
    };

    const handleQuantityChange = (itemId, qty) => {
        setFormData(prev => ({
            ...prev,
            work_quantities: {
                ...prev.work_quantities,
                [itemId]: parseInt(qty) || 0
            }
        }));
    };

    const handleImportProjectExcel = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Use xlsx to parse client-side for immediate UI feedback
        try {
            const L = (await import('xlsx')).default;
            const reader = new FileReader();
            reader.onload = (evt) => {
                const bstr = evt.target.result;
                const wb = L.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = L.utils.sheet_to_json(ws);

                const newIds = [];
                const newQtds = { ...formData.work_quantities };

                data.forEach(row => {
                    const nome = row['Nome'] || row['nome'] || row['Trabalho'] || row['trabalho'];
                    const qtd = parseInt(row['Quantidade'] || row['quantidade'] || row['Qtd'] || 1);

                    const item = availableWorkItems.find(i => i.nome.toLowerCase() === nome.toLowerCase());
                    if (item) {
                        if (!newIds.includes(item.id)) newIds.push(item.id);
                        newQtds[item.id] = qtd;
                    }
                });

                setFormData(prev => ({
                    ...prev,
                    tipo_trabalho: [...new Set([...prev.tipo_trabalho, ...newIds])],
                    work_quantities: newQtds
                }));
                liquidFeedback.notify(`Importados ${newIds.length} trabalhos com sucesso!`);
            };
            reader.readAsBinaryString(file);
        } catch (err) {
            console.error('Erro ao importar excel de projeto:', err);
            liquidFeedback.notify('Erro ao processar ficheiro Excel');
        } finally {
            e.target.value = null;
        }
    };

    const handleLocationSelect = async (loc) => {
        // Only update description if it's currently empty
        setFormData(prev => ({
            ...prev,
            latitude: loc.lat,
            longitude: loc.lng,
            descricao: prev.descricao.trim() === '' ? loc.displayName.split(',')[0] : prev.descricao
        }));

        if (mapInstanceRef.current && mainMarkerRef.current) {
            mapInstanceRef.current.flyTo([loc.lat, loc.lng], 18);
            mainMarkerRef.current.setLatLng([loc.lat, loc.lng]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const selectedWorkItems = availableWorkItems
            .filter(i => formData.tipo_trabalho.includes(i.id))
            .map(i => ({
                ...i,
                quantidade: formData.work_quantities[i.id] || 1
            }));

        const payload = {
            posicao: formData.posicao,
            cliente: formData.cliente,
            cliente_id: formData.cliente_id || null,
            data_inicio: formData.data_inicio,
            data_fim: formData.data_fim,
            descricao: formData.descricao,
            tipo_obra: formData.tipo_obra || 'obra',
            tipo_trabalho: selectedWorkItems.map(i => i.nome),
            latitude: formData.latitude,
            longitude: formData.longitude,
            cor: formData.cor,
            detalhes_trabalho: selectedWorkItems,
            observacoes: formData.observacoes,
            observacoes: formData.observacoes,
            gestor_cliente: formData.gestor_cliente,
            responsavel_obra_id: formData.responsavel_obra_id,
            seguranca_obra_id: formData.seguranca_obra_id,
            documentacao_validada: formData.documentacao_validada,
            necessidade_at: formData.necessidade_at,
            data_at: formData.data_at,
            hora_at: formData.hora_at,
            tipo_at: formData.tipo_at,
            nome_fiscalizacao: formData.nome_fiscalizacao,
            email_fiscalizacao: formData.email_fiscalizacao,
            telefone_fiscalizacao: formData.telefone_fiscalizacao,
            fiscalizacao_id: formData.fiscalizacao_id,
            gestor_cliente_id: formData.gestor_cliente_id,
            responsavel_cliente_id: formData.responsavel_cliente_id,
            map_data: {
                polygons: mapTools.polygons,
                icons: mapTools.icons
            }
        };

        console.log('Sending payload:', payload);

        try {
            const response = await api.post('/obras', payload);
            console.log('Response:', response.data);
            navigate('/obras');
        } catch (error) {
            console.error('Erro completo:', error);
            console.error('Response:', error.response?.data);
            liquidFeedback.notify(`Erro ao criar obra: ${error.response?.data?.message || error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="nova-obra-page">
            <div className="page-header">
                <div>
                    <h1><OrcamentoIcon name="building" fallback="🏗️" /> Nova Obra</h1>
                    <p>Planeamento e Localização</p>
                </div>
                <div className="header-actions" style={{ display: 'flex', gap: '10px' }}>
                    <button type="submit" className="btn-primary" disabled={loading} style={{ background: '#2196F3', color: 'white', padding: '8px 24px' }}>
                        {loading ? 'A processar...' : 'Gravar Obra'}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setShowWorkManager(true)}>
                        <OrcamentoIcon name="settings" fallback="⚙️" /> Gerir Trabalhos
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => navigate('/obras')}>
                        ← Voltar
                    </button>
                </div>
            </div>

            <div className="obra-form">
                <div className="form-layout">
                    <div className="form-section">
                        <h2><OrcamentoIcon name="document" fallback="📋" /> Dados da Operação</h2>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Posição *</label>
                                <input
                                    type="text"
                                    name="posicao"
                                    value={formData.posicao}
                                    onChange={handleChange}
                                    placeholder="Ex: 12345"
                                    required
                                />
                            </div>
                            <div className="form-group" style={{ flex: 2 }}>
                                <label>Nome da Obra / Local *</label>
                                <input
                                    type="text"
                                    name="descricao"
                                    value={formData.descricao}
                                    onChange={handleChange}
                                    placeholder="Ex: Escola Secundária, Centro Comercial..."
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Cliente</label>
                                <select name="cliente_id" value={formData.cliente_id} onChange={handleChange}>
                                    <option value="">-- Selecionar cliente --</option>
                                    {clientes.map(c => (
                                        <option key={c.id} value={c.id}>{c.nome}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Cor no Planeamento</label>
                                <div className="color-palette" style={{ display: 'flex', gap: '5px', marginTop: '5px', flexWrap: 'wrap' }}>
                                    {PREDEFINED_COLORS.map(c => (
                                        <div
                                            key={c}
                                            style={{
                                                width: '24px',
                                                height: '24px',
                                                backgroundColor: c,
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                border: formData.cor === c ? '2px solid #2d3748' : '2px solid transparent',
                                                boxShadow: formData.cor === c ? 'inset 0 0 0 2px white' : 'none',
                                                transition: 'all 0.2s'
                                            }}
                                            onClick={() => setFormData({ ...formData, cor: c })}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Início</label>
                                <input type="date" name="data_inicio" value={formData.data_inicio} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Fim</label>
                                <input type="date" name="data_fim" value={formData.data_fim} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Tipo</label>
                                <select name="tipo_obra" value={formData.tipo_obra} onChange={handleChange}>
                                    <option value="obra">Obra</option>
                                    <option value="manutencao">Manutenção</option>
                                    <option value="pendencia">Pendência</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group" style={{ flex: 1 }}>
                                <label>Observações</label>
                                <textarea
                                    name="observacoes"
                                    value={formData.observacoes}
                                    onChange={handleChange}
                                    placeholder="Notas adicionais sobre a obra..."
                                    style={{ width: '100%', minHeight: '80px', padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0' }}
                                />
                            </div>
                        </div>

                        <div style={{ borderTop: '1px solid #eee', paddingTop: '15px', marginTop: '10px' }}>
                            <label style={{ fontWeight: 'bold', marginBottom: '15px', display: 'block' }}><OrcamentoIcon name="document" fallback="📋" /> Detalhe de Obra</label>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', alignItems: 'end' }}>
                                {/* Coluna 1: Responsável Cliente */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div className="form-group">
                                        <label>Responsável Obra (Cliente)</label>
                                        <select
                                            name="responsavel_cliente_id"
                                            value={formData.responsavel_cliente_id}
                                            onChange={handleChange}
                                            className="form-control"
                                        >
                                            <option value="">Selecione...</option>
                                            {users.filter(u => u.tipo_utilizador === 'externo').map(u => (
                                                <option key={u.id} value={u.id}>{u.nome_completo}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '11px', color: '#718096' }}>Email</label>
                                        <input
                                            type="email"
                                            value={formData.email_responsavel_cliente || ''}
                                            readOnly
                                            className="form-control"
                                            style={{ backgroundColor: '#f7fafc', fontSize: '12px' }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '11px', color: '#718096' }}>Telefone</label>
                                        <input
                                            type="text"
                                            value={formData.telefone_responsavel_cliente || ''}
                                            readOnly
                                            className="form-control"
                                            style={{ backgroundColor: '#f7fafc', fontSize: '12px' }}
                                        />
                                    </div>
                                </div>

                                {/* Coluna 2: Responsável Obra (Interno) */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div className="form-group">
                                        <label>Responsável Obra (Interno)</label>
                                        <select
                                            name="responsavel_obra_id"
                                            value={formData.responsavel_obra_id}
                                            onChange={handleChange}
                                            className="form-control"
                                        >
                                            <option value="">Selecione Utilizador...</option>
                                            {users.map(u => (
                                                <option key={u.id} value={u.id}>{u.nome_completo}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '11px', color: '#718096' }}>Email</label>
                                        <input
                                            type="email"
                                            value={formData.email_responsavel || ''}
                                            readOnly
                                            className="form-control"
                                            style={{ backgroundColor: '#f7fafc', fontSize: '12px' }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '11px', color: '#718096' }}>Telefone</label>
                                        <input
                                            type="text"
                                            value={formData.telefone_responsavel || ''}
                                            readOnly
                                            className="form-control"
                                            style={{ backgroundColor: '#f7fafc', fontSize: '12px' }}
                                        />
                                    </div>
                                </div>

                                {/* Coluna 3: Segurança Obra */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div className="form-group">
                                        <label>Segurança Obra</label>
                                        <select
                                            name="seguranca_obra_id"
                                            value={formData.seguranca_obra_id}
                                            onChange={handleChange}
                                            className="form-control"
                                        >
                                            <option value="">Selecione Utilizador...</option>
                                            {users.map(u => (
                                                <option key={u.id} value={u.id}>{u.nome_completo}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '11px', color: '#718096' }}>Email</label>
                                        <input
                                            type="email"
                                            value={formData.email_seguranca || ''}
                                            readOnly
                                            className="form-control"
                                            style={{ backgroundColor: '#f7fafc', fontSize: '12px' }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '11px', color: '#718096' }}>Telefone</label>
                                        <input
                                            type="text"
                                            value={formData.telefone_seguranca || ''}
                                            readOnly
                                            className="form-control"
                                            style={{ backgroundColor: '#f7fafc', fontSize: '12px' }}
                                        />
                                    </div>
                                </div>

                                {/* Coluna 4: Fiscalização */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <div className="form-group">
                                        <label>Fiscalização</label>
                                        <select
                                            name="fiscalizacao_id"
                                            value={formData.fiscalizacao_id}
                                            onChange={handleChange}
                                            className="form-control"
                                        >
                                            <option value="">Selecione Fiscal...</option>
                                            {users.map(u => (
                                                <option key={u.id} value={u.id}>{u.nome_completo}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '11px', color: '#718096' }}>Email</label>
                                        <input
                                            type="email"
                                            name="email_fiscalizacao"
                                            value={formData.email_fiscalizacao}
                                            readOnly
                                            className="form-control"
                                            style={{ backgroundColor: '#f7fafc', fontSize: '12px' }}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label style={{ fontSize: '11px', color: '#718096' }}>Telefone</label>
                                        <input
                                            type="text"
                                            name="telefone_fiscalizacao"
                                            value={formData.telefone_fiscalizacao}
                                            readOnly
                                            className="form-control"
                                            style={{ backgroundColor: '#f7fafc', fontSize: '12px' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="form-row" style={{ marginTop: '20px' }}>
                                <div className="form-group">
                                    <label>Doc. Validada?</label>
                                    <select name="documentacao_validada" value={formData.documentacao_validada} onChange={handleChange}>
                                        <option value="">Selecione...</option>
                                        <option value="s">Sim</option>
                                        <option value="n">Não</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Necessidade de AT?</label>
                                    <select name="necessidade_at" value={formData.necessidade_at} onChange={handleChange}>
                                        <option value="">Selecione...</option>
                                        <option value="s">Sim</option>
                                        <option value="n">Não</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Data Trabalho AT</label>
                                    <input type="date" name="data_at" value={formData.data_at} onChange={handleChange} />
                                </div>
                                <div className="form-group">
                                    <label>Hora Emissão AT</label>
                                    <input type="time" name="hora_at" value={formData.hora_at} onChange={handleChange} />
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <label>Trabalhos a Executar</label>
                            <div data-liquid-actions="true" style={{ display: 'flex', gap: '8px' }}>
                                <input
                                    type="file"
                                    id="import-project-works"
                                    style={{ display: 'none' }}
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleImportProjectExcel}
                                />
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    style={{ padding: '2px 8px', fontSize: '11px' }}
                                    onClick={() => document.getElementById('import-project-works').click()}
                                >
                                    <OrcamentoIcon name="upload" fallback="📥" /> Importar Excel
                                </button>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    style={{ padding: '2px 8px', fontSize: '11px' }}
                                    onClick={() => setShowWorkManager(true)}
                                >
                                    <OrcamentoIcon name="settings" fallback="⚙️" /> Gerir Catálogo
                                </button>
                            </div>
                        </div>
                        <div className="work-items-grid">
                            {[...new Set(availableWorkItems.map(i => i.categoria))].map(cat => {
                                const daCategoria = availableWorkItems.filter(i => i.categoria === cat);
                                const selecionados = daCategoria.filter(i => formData.tipo_trabalho.includes(i.id));
                                const disponiveis = daCategoria.filter(i => !formData.tipo_trabalho.includes(i.id));
                                return (
                                    <div key={cat} className="work-category">
                                        <h4>{cat}{selecionados.length > 0 ? ` (${selecionados.length})` : ''}</h4>
                                        {selecionados.map(item => (
                                            <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 6, background: '#ebf8ff', marginBottom: 4 }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, fontSize: 13, color: '#2c5282' }}>
                                                    <span><LiquidText>{item.icone}</LiquidText></span>
                                                    <span>{item.nome}</span>
                                                </span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    title="Quantidade"
                                                    value={formData.work_quantities[item.id] || 1}
                                                    onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                    style={{ width: 45, padding: '2px 4px', border: '1px solid #3182ce', borderRadius: 4, background: '#fff', textAlign: 'center', fontWeight: 'bold' }}
                                                />
                                                <button type="button" title="Remover" onClick={() => handleWorkItemToggle(item.id)}
                                                    style={{ border: 'none', background: 'none', color: '#e53e3e', fontWeight: 700, cursor: 'pointer', padding: '0 2px' }}><DeleteIcon /></button>
                                            </div>
                                        ))}
                                        {disponiveis.length > 0 && (
                                            <select value="" onChange={(e) => {
                                                const id = parseInt(e.target.value, 10);
                                                if (!Number.isNaN(id)) handleWorkItemToggle(id);
                                            }} style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px dashed #a0aec0', borderRadius: 6, color: '#4a5568', background: '#fafbfc', cursor: 'pointer' }}>
                                                <option value="">➕ Adicionar trabalho…</option>
                                                {disponiveis.map(i => (
                                                    <option key={i.id} value={i.id}>{i.icone ? `${i.icone} ` : ''}{i.nome}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="form-section map-section">
                        <h2><LiquidText>🗺️ Localização e Planta</LiquidText></h2>
                        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '10px' }}>
                            Utilize a procura abaixo para centrar o mapa na morada correta:
                        </p>
                        <MapSearch onLocationSelect={handleLocationSelect} />

                        {/* Drawing Tools */}
                        <div className="map-tools">
                            <button
                                type="button"
                                className={`tool-btn ${mapTools.activeTool === 'polygon-red' ? 'active' : ''}`}
                                onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'polygon-red' ? null : 'polygon-red' }))}
                                title="Desenhar área vermelha"
                            ><LiquidText>
                                🔴 Polígono Vermelho
                            </LiquidText></button>
                            <button
                                type="button"
                                className={`tool-btn ${mapTools.activeTool === 'polygon-yellow' ? 'active' : ''}`}
                                onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'polygon-yellow' ? null : 'polygon-yellow' }))}
                                title="Desenhar área amarela"
                            ><LiquidText>
                                🟡 Polígono Amarelo
                            </LiquidText></button>
                            <button
                                type="button"
                                className={`tool-btn ${mapTools.activeTool === 'icon-cone' ? 'active' : ''}`}
                                onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-cone' ? null : 'icon-cone' }))}
                                title="Colocar cone"
                            ><LiquidText>
                                🚧 Cone
                            </LiquidText></button>
                            <button
                                type="button"
                                className={`tool-btn ${mapTools.activeTool === 'icon-health' ? 'active' : ''}`}
                                onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-health' ? null : 'icon-health' }))}
                                title="Cruz Saúde"
                            ><LiquidText>
                                ➕ Saúde
                            </LiquidText></button>
                            <button
                                type="button"
                                className={`tool-btn ${mapTools.activeTool === 'icon-extinguisher' ? 'active' : ''}`}
                                onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-extinguisher' ? null : 'icon-extinguisher' }))}
                                title="Extintor"
                            ><LiquidText>
                                🧯 Extintor
                            </LiquidText></button>
                            <button
                                type="button"
                                className={`tool-btn ${mapTools.activeTool === 'icon-start' ? 'active' : ''}`}
                                onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-start' ? null : 'icon-start' }))}
                                title="Local a Intervencionar"
                            >
                                <OrcamentoIcon name="warning" fallback="⚠️" /> Local Interv.
                            </button>
                            <button
                                type="button"
                                className={`tool-btn ${mapTools.activeTool === 'icon-end' ? 'active' : ''}`}
                                onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-end' ? null : 'icon-end' }))}
                                title="Fim de Obra"
                            ><LiquidText>
                                🛑 Fim
                            </LiquidText></button>
                            <button
                                type="button"
                                className={`tool-btn ${mapTools.activeTool === 'icon-warning' ? 'active' : ''}`}
                                onClick={() => setMapTools(prev => ({ ...prev, activeTool: prev.activeTool === 'icon-warning' ? null : 'icon-warning' }))}
                                title="Sinalização"
                            >
                                <OrcamentoIcon name="warning" fallback="⚠️" /> Sinalização
                            </button>
                            <button
                                type="button"
                                className="tool-btn tool-clear"
                                onClick={() => setMapTools({ activeTool: null, polygons: { red: [], yellow: [] }, icons: [], drawnLayers: [] })}
                                title="Limpar desenhos"
                            >
                                <OrcamentoIcon name="trash" fallback="🗑️" /> Limpar
                            </button>
                        </div>

                        {/* Drawing Instructions */}
                        {mapTools.activeTool?.startsWith('polygon') && (
                            <div style={{
                                padding: '8px 12px',
                                background: '#E3F2FD',
                                border: '1px solid #2196F3',
                                borderRadius: '4px',
                                marginBottom: '10px',
                                fontSize: '13px',
                                color: '#1976D2',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}>
                                <span><LiquidText>
                                    📌 Clique no mapa para adicionar pontos
                                    </LiquidText>{currentPolygonPoints.length > 0 && (
                                        <strong> ({currentPolygonPoints.length} pontos)</strong>
                                    )}
                                </span>
                                {currentPolygonPoints.length >= 3 && (
                                    <button
                                        type="button"
                                        onClick={finishPolygon}
                                        style={{
                                            background: '#4CAF50',
                                            color: 'white',
                                            border: 'none',
                                            padding: '6px 16px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontWeight: '600',
                                            fontSize: '12px'
                                        }}
                                    ><LiquidText>
                                        ✓ Finalizar Polígono
                                    </LiquidText></button>
                                )}
                            </div>
                        )}

                        <div ref={mapRef} className="map-container" id="obra-map"></div>

                        <div className="map-info">
                            <small>Arraste o marcador para ajustar a localização</small>
                            <div className="coords">
                                {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {showWorkManager && (
                <WorkItemsManager
                    onClose={() => { setShowWorkManager(false); fetchWorkItems(); }}
                    onUpdate={fetchWorkItems}
                />
            )}
        </form>
    );
};

export default NovaObra;
